<template>
    <button
        :class="[
            // Base styles
            'flex cursor-pointer items-center gap-1.5',
            'transition-[border-color,background-color] duration-150',

            // Variant: pill (horizontal, compact)
            variant === 'pill' && [
                'flex-row rounded-md px-3 py-1.5 text-xs font-medium',
                !isActive && 'text-theme-text',
            ],

            // Variant: card (vertical, with icon + label)
            variant === 'card' && [
                'flex-col rounded-lg px-2 py-2.5 text-center',
                !isActive && 'text-theme-toolbar-icon',
            ],

            // Variant: icon (compact, icon only)
            variant === 'icon' && [
                'flex-col items-center justify-center rounded-lg px-2 py-1',
                !isActive && 'text-theme-toolbar-icon',
            ],

            // Border states
            isActive ? 'border-2 border-theme-accent' : 'border border-theme-border',

            // Active state (class for styling child labels)
            isActive && 'is-active bg-theme-accent-bg text-theme-accent',

            // High contrast: thicker border when active
            isActive && 'high-contrast-dark:border-[3px]',

            // Hover states (only when not active)
            !isActive && variant === 'pill' && 'hover:border-theme-accent',
            !isActive &&
                (variant === 'card' || variant === 'icon') && [
                    'hover:bg-black/[0.04]',
                    'dark:hover:bg-white/[0.06]',
                    'high-contrast-dark:hover:bg-white/[0.08]',
                ],
        ]"
        :aria-pressed="isActive"
        v-bind="$attrs"
        @click="$emit('click', $event)"
    >
        <slot />
    </button>
</template>

<script>
import { computed } from 'vue';

/**
 * Reusable settings selection button with two variants.
 * Supports theme colors, dark mode, and high contrast accessibility.
 *
 * @example Pill variant (horizontal, for text options like fonts)
 * <SettingsButton variant="pill" :is-active="selected" @click="select">
 *     Option Label
 * </SettingsButton>
 *
 * @example Card variant (vertical, for options with icons)
 * <SettingsButton variant="card" :is-active="selected" @click="select">
 *     <IconComponent />
 *     <SettingsButtonLabel>Label</SettingsButtonLabel>
 * </SettingsButton>
 */
export default {
    name: 'SettingsButton',
    provide() {
        return {
            settingsButtonActive: computed(() => this.isActive),
        };
    },
    inheritAttrs: false,
    props: {
        variant: {
            type: String,
            default: 'pill',
            validator: value => ['pill', 'card', 'icon'].includes(value),
        },
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click'],
};
</script>
